/* ============================================================================
   CY MEGA TABS
   Drives the desktop mega panels: the tab rail swaps card decks, and each deck
   is a scroll-snap carousel paged by its arrows, by hovering them, or by
   dragging the track with the mouse.

   WHY EVERYTHING IS BOUND DIRECTLY TO ELEMENTS, NOT DELEGATED FROM DOCUMENT.
   The theme closes an open dropdown from a document-level click handler, and a
   click on a carousel arrow was reaching it - so paging the carousel slammed
   the whole panel shut. stopPropagation only helps if it runs before the
   event arrives at document, which means the listener must sit on the element
   itself; a delegated listener on document cannot shield anything, because by
   then every other document listener fires regardless. So the tabs, arrows
   and tracks each get their own listeners, and bind() re-runs on
   shopify:section:load for the theme editor. The one delegated listener kept
   is the rail's hover preview, which needs no shielding.

   The owner's reported bug also drove the interaction model: arrows page on
   HOVER (with a slow repeat while you stay on them), so a shopper never has
   to click inside the panel at all, and the track drags with the mouse like
   it swipes on a phone.
   ========================================================================== */
(function () {
  'use strict';

  var HOVER_DELAY = 90;      /* rail: pointer must settle before a tab switches */
  var HOVER_REPEAT = 900;    /* arrows: repeat interval while hovered */
  var hoverTimer = null;

  function tabsIn(root) {
    return Array.prototype.slice.call(root.querySelectorAll('.cy-mega__tab'));
  }

  function panelFor(tab) {
    var id = tab.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  /* ---- tab selection ---------------------------------------------------- */

  function select(tab, focus) {
    var root = tab.closest('[data-cy-tabs]');
    if (!root || tab.classList.contains('is-active')) {
      if (focus) tab.focus();
      return;
    }
    tabsIn(root).forEach(function (t) {
      var on = t === tab;
      t.classList.toggle('is-active', on);
      /* No aria-selected and no roving tabindex: the rail is a list of links,
         not a tablist. aria-selected is not valid on a link, and a roving
         tabindex would drop every row but one out of the Tab order - on a
         rail whose rows are now destinations, that would hide them from
         keyboard users entirely. `is-active` still marks which deck is up. */
      var p = panelFor(t);
      if (p) {
        if (on) { p.removeAttribute('hidden'); resetDeck(p); }
        else p.setAttribute('hidden', '');
      }
    });
    if (focus) tab.focus();
  }

  /* hover preview on the rail - delegated, pointerover bubbles */
  document.addEventListener('pointerover', function (e) {
    var tab = e.target.closest && e.target.closest('.cy-mega__tab');
    if (!tab) return;
    if (e.pointerType === 'touch') return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(function () { select(tab, false); }, HOVER_DELAY);
  });
  document.addEventListener('pointerout', function (e) {
    if (e.target.closest && e.target.closest('.cy-mega__tab')) clearTimeout(hoverTimer);
  });

  /* Arrow / Home / End move along the rail. This was APG tablist support;
     the rail is a list of links now, but stepping through it with the arrows
     is still the right convenience, and Enter is left untouched so a focused
     row navigates the way any link does. */
  document.addEventListener('keydown', function (e) {
    var tab = e.target.closest && e.target.closest('.cy-mega__tab');
    if (!tab) return;
    var root = tab.closest('[data-cy-tabs]');
    if (!root) return;
    var all = tabsIn(root);
    var i = all.indexOf(tab);
    var next = null;

    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = all[(i + 1) % all.length];
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = all[(i - 1 + all.length) % all.length];
    else if (e.key === 'Home') next = all[0];
    else if (e.key === 'End') next = all[all.length - 1];
    else return;

    e.preventDefault();
    select(next, true);
  });

  /* A panel closing should leave the rail where it started. `e.target ===
     root` is load-bearing: pointerleave does not bubble, but a capture
     listener on document receives it for every descendant, and reacting to
     all of them was the reset-to-first-tab glitch. */
  document.addEventListener('pointerleave', function (e) {
    var root = e.target;
    if (!root || !root.hasAttribute || !root.hasAttribute('data-cy-tabs')) return;
    clearTimeout(hoverTimer);
    var all = tabsIn(root);
    if (all.length) select(all[0], false);
  }, true);

  /* ---- the deck: paging, arrows, drag ----------------------------------- */

  /* Whole views per step; the browser clamps at both ends. No `behavior`
     here - the track's CSS says smooth, and the reduced-motion query says
     auto, and script passing either would override both. */
  function page(track, dir) {
    track.scrollBy({ left: dir * track.clientWidth });
  }

  /* 2026-10-23 (speed, MEDIA-SPEED-PLAN phase 4, owner unlock): READ, then write. measureNav only reads the track; applyNav writes the
     arrows, and only a value that changed. At load every deck of every (closed) mega menu was synced one after another, each write
     forcing the next deck's read to re-style the page: 1.9 to 2.4 s of a phone's main thread. Now one pass reads all decks, one pass
     writes, and that load-time pass waits for an idle moment (the menus are closed then). An open menu behaves exactly as before. */
  function measureNav(deck) {
    if (!deck) return null;
    var track = deck.querySelector('[data-cy-track]');
    var prev = deck.querySelector('[data-cy-prev]');
    var next = deck.querySelector('[data-cy-next]');
    if (!track || !prev || !next) return null;
    /* 2px slack: scrollLeft is fractional at fractional card widths */
    var max = track.scrollWidth - track.clientWidth;
    return { prev: prev, next: next, atStart: track.scrollLeft <= 2, atEnd: track.scrollLeft >= max - 2 };
  }
  function applyNav(m) {
    if (!m) return;
    if (m.prev.disabled !== m.atStart) m.prev.disabled = m.atStart;
    if (m.next.disabled !== m.atEnd) m.next.disabled = m.atEnd;
  }
  function syncNav(deck) { applyNav(measureNav(deck)); }
  function syncAll(decks) { decks.map(measureNav).forEach(applyNav); }
  var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 200); };

  function resetDeck(deck) {
    var track = deck.querySelector('[data-cy-track]');
    if (track) track.scrollLeft = 0;
    syncNav(deck);
  }

  /* arrows drive the track while the pointer rests on them */
  function bindNav(btn) {
    var hold = null;
    function fire() {
      if (btn.disabled) { clearInterval(hold); return; }
      var deck = btn.closest('.cy-mega__deck');
      var track = deck && deck.querySelector('[data-cy-track]');
      if (!track) return;
      page(track, btn.hasAttribute('data-cy-prev') ? -1 : 1);
      syncNav(deck);
    }
    btn.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'touch') return;
      fire();
      clearInterval(hold);
      hold = setInterval(fire, HOVER_REPEAT);
    });
    btn.addEventListener('pointerleave', function () { clearInterval(hold); });
    /* click still works, shielded so the theme's document handler - which
       closes the dropdown on any click it sees - never hears about it */
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
    });
    ['pointerdown', 'pointerup'].forEach(function (t) {
      btn.addEventListener(t, function (e) { e.stopPropagation(); });
    });
  }

  /* mouse drag-to-scroll - the phone swipe, on the desktop. Touch is left
     alone: the track is a native scroll container and touch already works. */
  function bindTrack(track) {
    var start = null;
    var dragged = false;

    track.addEventListener('pointerdown', function (e) {
      e.stopPropagation();                    /* shield the theme's close-on-click */
      dragged = false;                        /* stale flag from a drag that ended off-element */
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      start = { x: e.clientX, left: track.scrollLeft, id: e.pointerId };
    });

    track.addEventListener('pointermove', function (e) {
      if (!start) return;
      var dx = e.clientX - start.x;
      if (!dragged && Math.abs(dx) < 6) return;      /* still a click, not a drag */
      if (!dragged) {
        dragged = true;
        track.setPointerCapture(start.id);
        track.classList.add('is-dragging');          /* kills snap + smooth while held */
      }
      track.scrollLeft = start.left - dx;
    });

    function release(e) {
      if (!start) return;
      if (e && e.stopPropagation) e.stopPropagation();
      start = null;
      track.classList.remove('is-dragging');         /* snap re-engages and settles */
      syncNav(track.closest('.cy-mega__deck'));
    }
    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);

    /* the click that follows a drag must neither navigate nor reach the
       theme; a genuine click on a card still navigates normally */
    track.addEventListener('click', function (e) {
      e.stopPropagation();
      if (dragged) { e.preventDefault(); dragged = false; }
    }, true);
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });

    track.addEventListener('scroll', function () {
      syncNav(track.closest('.cy-mega__deck'));
    });
  }

  /* Tabs are shielded the same way - a click on a rail tab was reaching the
     same document handler an arrow click was. stopPropagation still does that
     job; what changed (owner 2026-08-31) is that the rail rows are now <a>
     elements with a real destination, so the click must be allowed to
     NAVIGATE. preventDefault used to swallow it unconditionally, which is
     exactly what would make a link do nothing. It is now called only for a
     row with no href - the modifier keys are left alone so ctrl/cmd/middle
     click still open in a new tab. */
  function bindTab(tab) {
    tab.addEventListener('click', function (e) {
      e.stopPropagation();
      var href = tab.getAttribute('href');
      if (!href) {
        e.preventDefault();
        select(tab, false);
      }
    });
    ['pointerdown', 'pointerup'].forEach(function (t) {
      tab.addEventListener(t, function (e) { e.stopPropagation(); });
    });
  }

  function bind() {
    document.querySelectorAll('[data-cy-tabs]').forEach(function (root) {
      if (root.__cyBound) return;
      root.__cyBound = true;
      root.querySelectorAll('.cy-mega__tab').forEach(bindTab);
      root.querySelectorAll('.cy-mega__nav').forEach(bindNav);
      root.querySelectorAll('[data-cy-track]').forEach(bindTrack);
      var decks = [].slice.call(root.querySelectorAll('.cy-mega__deck'));
      idle(function () { syncAll(decks); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
  document.addEventListener('shopify:section:load', bind);
}());

/* ============================================================================
   ACTIVE MAIN-MENU TAB (2026-09-28, owner: "make rule for active menu tab also in main menu")
   The theme never marks a top-level tab active (its summaries carry no current-page state), so this block
   picks the tab of the section the visitor is in and gives it .cy-nav-active (style: custom.css, top-level nav)
   + aria-current. The rule reads the menu itself — nothing hardcoded:
     1. the page is the tab's own link or any link inside the tab's mega panel (e.g. /collections/gaming-mouse,
        /products/xeus-gaming-mouse, /pages/faq);
     2. otherwise a breadcrumb link of the page matches (a product page -> its collection);
     3. otherwise a link of the page's sticky page menu matches (policy / form pages -> Support).
   The first step that finds exactly one tab wins; no match (home, search, cart) = no active tab.
   ============================================================================ */
(function () {
  'use strict';
  function norm(href) {
    try { var p = new URL(href, location.href).pathname.replace(/\/+$/, ''); return p.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, '') || '/'; } catch (e) { return ''; }
  }
  function mark() {
    var lis = [].slice.call(document.querySelectorAll('#hdt-nav-ul > li')); if (!lis.length) return;
    var tabs = lis.map(function (li) {
      var top = li.querySelector(':scope > a.hdt-menu-link, :scope > hdt-header-menu > details > summary.hdt-menu-link');
      var urls = {};
      [].forEach.call(li.querySelectorAll('a[href]'), function (a) { var p = norm(a.getAttribute('href')); if (p && p !== '/') urls[p] = 1; });
      var own = top && (top.getAttribute('href') || (top.closest('details') && top.closest('details').getAttribute('data-href')));
      if (own) { var o = norm(own); if (o && o !== '/') urls[o] = 1; }
      return { top: top, urls: urls };
    }).filter(function (t) { return t.top; });
    function pick(paths) {
      var hits = tabs.filter(function (t) { return paths.some(function (p) { return t.urls[p]; }); });
      return hits.length === 1 ? hits[0] : null;
    }
    var list = function (sel) { return [].map.call(document.querySelectorAll(sel), function (a) { return norm(a.getAttribute('href')); }).filter(function (p) { return p && p !== '/'; }); };
    var here = norm(location.href);
    var tab = (here !== '/' && pick([here]))
      || pick(list('nav[aria-label*="readcrumb"] a[href], .hdt-breadcrumb a[href], .cys-crumb a[href], [class*="pdp-crumb"] a[href]'))
      || pick(list('[data-cys-subnav] a[href]'));
    tabs.forEach(function (t) {
      var on = t === tab;
      t.top.classList.toggle('cy-nav-active', on);
      if (on) t.top.setAttribute('aria-current', 'page'); else if (t.top.getAttribute('aria-current') === 'page') t.top.removeAttribute('aria-current');
    });
  }
  /* the hover / active line sits on the header bar's bottom edge, like the sticky page menu's (owner 2026-09-28:
     "same thickness … and on menu bottom corner"): --cy-nav-drop = distance from the link text to the bottom of
     the header bar, re-measured whenever the header changes size */
  function drop() {
    var ul = document.getElementById('hdt-nav-ul'); if (!ul) return;
    var bar = ul.closest('.hdt-main-header') || ul.closest('header'), t = ul.querySelector('.hdt-menu-link-text');
    if (!bar || !t) return;
    var d = bar.getBoundingClientRect().bottom - t.getBoundingClientRect().bottom;
    var v = Math.round(d * 10) / 10 + 'px';   /* written only when it changes (2026-10-23 speed: every write re-styles the menu) */
    if (d > 0 && d < 80 && ul.style.getPropertyValue('--cy-nav-drop') !== v) ul.style.setProperty('--cy-nav-drop', v);
  }
  function init() {
    mark(); drop();
    var ul = document.getElementById('hdt-nav-ul'), bar = ul && (ul.closest('.hdt-main-header') || ul.closest('header'));
    if (bar && window.ResizeObserver && !bar.dataset.cyDrop) { bar.dataset.cyDrop = '1'; new ResizeObserver(drop).observe(bar); }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  window.addEventListener('resize', drop);
  window.addEventListener('load', drop);
  document.addEventListener('shopify:section:load', init);
}());
